'use client'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { useEscapeKey } from '../../hooks/useEscapeKey'
import ReactDOM from 'react-dom'

import { Box, SystemStyleObject, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { CloseIcon } from '../Icon/CloseIcon'
import {
  PopoverPlacement,
  placementStyles,
  popoverCloseButtonStyles,
  popoverContentStyles,
} from './Popover.styles'

export type { PopoverPlacement }

export type PopoverProps = {
  /**
   * Trigger element. Receives `onClick` (toggles the panel) and `isOpen` so it
   * can reflect state. Rendered inline as the anchor the panel positions from.
   */
  trigger: (props: { onClick: () => void; isOpen: boolean }) => React.ReactNode
  /** Arbitrary panel content. */
  children: React.ReactNode
  /**
   * Accessible name for the panel. The panel is a `role="dialog"`, so screen
   * readers announce this as its purpose — always provide one for a panel with
   * rich content.
   */
  ariaLabel?: string
  colorScheme?: ColorScheme
  /** Where the panel opens relative to the trigger. Defaults to bottom-start. */
  placement?: PopoverPlacement
  /**
   * Controlled open state. Omit to let the Popover manage its own open state;
   * provide it (with `onOpenChange`) to drive the panel from the parent.
   */
  isOpen?: boolean
  /**
   * Fired whenever the open state should change — trigger click, outside click,
   * Escape, or the close button. Required to drive the controlled form.
   */
  onOpenChange?: (open: boolean) => void
  /** Renders a close (×) button pinned to the top-right of the panel. */
  showCloseButton?: boolean
  /** Override the panel z-index. Defaults to 10. */
  zIndex?: number
  /** Override the panel background (e.g. to match the surface it opens over,
   * like a modal). Any Chakra color value or token. */
  bg?: string
  /** Style overrides merged onto the panel surface. */
  contentStyleOverrides?: SystemStyleObject
  /**
   * Render the panel in a `document.body` portal (position: fixed, anchored to
   * the trigger) instead of inline. Use when an ancestor clips overflow — the
   * inline panel is `position: absolute` and gets cut off by a scroll
   * container; the portalled panel escapes it. Recomputes on scroll/resize.
   */
  usePortal?: boolean
}

type PanelPos = { top: number; left: number; transform?: string }

/** Viewport coordinates for the portalled panel, per placement. */
const calcPos = (rect: DOMRect, placement: PopoverPlacement): PanelPos => {
  const GAP = 8
  switch (placement) {
    case 'bottom-end':
      return {
        top: rect.bottom + GAP,
        left: rect.right,
        transform: 'translateX(-100%)',
      }
    case 'top-start':
      return {
        top: rect.top - GAP,
        left: rect.left,
        transform: 'translateY(-100%)',
      }
    case 'top-end':
      return {
        top: rect.top - GAP,
        left: rect.right,
        transform: 'translate(-100%, -100%)',
      }
    case 'left-start':
      return {
        top: rect.top,
        left: rect.left - GAP,
        transform: 'translateX(-100%)',
      }
    case 'right-start':
      return { top: rect.top, left: rect.right + GAP }
    default: // bottom-start
      return { top: rect.bottom + GAP, left: rect.left }
  }
}

/**
 * Anchored content panel: a trigger plus a floating panel positioned relative
 * to it. Content-agnostic (unlike ActionMenu, which is item-list only), so it
 * suits rich panels — forms, previews, confirmations. Closes on outside click
 * and Escape; supports both controlled and uncontrolled open state.
 */
export const Popover = ({
  trigger,
  children,
  ariaLabel,
  colorScheme = 'dark',
  placement = 'bottom-start',
  isOpen,
  onOpenChange,
  showCloseButton = false,
  zIndex,
  bg,
  contentStyleOverrides,
  usePortal = false,
}: PopoverProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [isInternalOpen, setIsInternalOpen] = useState(false)
  const [pos, setPos] = useState<PanelPos | null>(null)
  // Gates the portal render so SSR and the first client render produce the same
  // tree (no panel), avoiding a hydration mismatch; the portal only mounts once
  // we're on the client. Never branch the portal on `typeof document` alone —
  // that renders inline on the server and portalled on the client.
  const [isMounted, setIsMounted] = useState(false)
  const isControlled = isOpen !== undefined
  const isPanelOpen = isControlled ? isOpen : isInternalOpen

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setIsInternalOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  const handleTriggerClick = () => setOpen(!isPanelOpen)

  // Portal mode: track the trigger's viewport position while open so the fixed
  // panel stays anchored through scroll/resize.
  useLayoutEffect(() => {
    if (!usePortal || !isPanelOpen) {
      setPos(null)
      return
    }
    const update = () => {
      if (wrapperRef.current) {
        setPos(calcPos(wrapperRef.current.getBoundingClientRect(), placement))
      }
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [usePortal, isPanelOpen, placement])

  // Close on outside click. The portalled panel lives outside the wrapper, so
  // check both the wrapper and the panel before treating a click as outside.
  useEffect(() => {
    if (!isPanelOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        !wrapperRef.current?.contains(target) &&
        !panelRef.current?.contains(target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isPanelOpen, setOpen])

  // Close on Escape, without letting a surrounding drawer/modal close too.
  const handleEscape = useCallback(() => setOpen(false), [setOpen])
  useEscapeKey(handleEscape, isPanelOpen)

  const panel = (
    <Box
      aria-label={ariaLabel}
      ref={panelRef}
      role='dialog'
      {...popoverContentStyles(colorScheme)}
      {...(usePortal
        ? {
            position: 'fixed',
            top: `${pos?.top ?? 0}px`,
            left: `${pos?.left ?? 0}px`,
            transform: pos?.transform,
            visibility: pos ? 'visible' : 'hidden',
          }
        : placementStyles[placement])}
      zIndex={zIndex ?? 10}
      {...(bg !== undefined && { bg })}
      {...contentStyleOverrides}
    >
      {showCloseButton && (
        <chakra.button
          aria-label='Close'
          onClick={() => setOpen(false)}
          type='button'
          {...popoverCloseButtonStyles(colorScheme)}
        >
          <CloseIcon color='currentColor' />
        </chakra.button>
      )}
      {children}
    </Box>
  )

  return (
    <Box display='inline-block' position='relative' ref={wrapperRef}>
      {trigger({ onClick: handleTriggerClick, isOpen: isPanelOpen })}
      {isPanelOpen &&
        (usePortal
          ? isMounted && ReactDOM.createPortal(panel, document.body)
          : panel)}
    </Box>
  )
}
